import { useEffect, useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { $countryProvincesStore, $countryStore, $recipientCreateStore, $recipientMandatoryFieldsStore, $transferCurrenciesStore } from '@lib/store';
import { TransferRecipientAccountTypeEnum, TransferRecipientTypeEnum } from '@shared/api/rest/recipients';
import { ConfirmModal } from '../../../../payments/subPages/widgets';
import { Button } from '@shared/widgets';
import ConfirmIcon from '@assets/icons/confirm.svg';
import { AccountFormBlock, AddressFormBlock, CompanyFormBlock } from '../../../../payments/subPages/formItem';
import type { AccountFormDataType, AddressFormDataType, CompanyFormDataType } from '../../../../payments/subPages/formItem';

export interface IFormDataBusinessCreate {
  bankCountry?: string;
  currency: string;
  accountNumber: string;
  accountType?: TransferRecipientAccountTypeEnum;
  iban: string;
  bic: string;
  firstName: string;
  lastName: string;
  bankName: string;
  idNumber: string;
  companyName: string;
  email: string;
  address: string;
  postCode: string;
  city: string;
  companyCountry: string;
  bankProvince?: string;
  bankAddress: string;
  bankCity: string;
  branchCode: string;
  province?: string;
}

export const BusinessRecipient = () => {
  const recipientStore = useUnit($recipientCreateStore.store);

  const countryStore = useUnit($countryStore.store);

  useEffect(() => {
    $transferCurrenciesStore.events.update();
    $countryStore.events.update();
    $countryProvincesStore.events.update();
    return () => {
      $recipientMandatoryFieldsStore.events.clear();
      $recipientCreateStore.events.clear();
    };
  }, []);

  const formControl = useForm({
    mode: 'onChange',
    defaultValues: {
      bankCountry: undefined,
      currency: 'EUR',
      accountNumber: '',
      accountType: undefined,
      iban: '',
      bic: '',
      firstName: '',
      lastName: '',
      idNumber: '',
      bankName: '',
      companyName: '',
      email: '',
      address: '',
      postCode: '',
      city: '',
      companyCountry: '',
      bankProvince: undefined,
      bankAddress: '',
      bankCity: '',
      branchCode: '',
      province: undefined,
      amount: undefined,
      paymentReason: '',
    } as IFormDataBusinessCreate,
  });

  const bankCountry = formControl.watch('bankCountry');
  const companyCountry = formControl.watch('companyCountry');
  const currency = formControl.watch('currency');

  const onSubmit = async (data: IFormDataBusinessCreate) => await onCreateRecipient(data);
  const onCreateRecipient = async (data: IFormDataBusinessCreate) => {
    if (currency == null || data.accountType == null) return null;

    const response = await $recipientCreateStore.events.create({
      type: TransferRecipientTypeEnum.Business,
      countryCode: companyCountry,
      currency: currency!,
      accountNumber: data.accountNumber,
      accountType: data.accountType!,
      iban: data.iban,
      bicOrSwiftCode: data.bic,
      firstName: undefined,
      lastName: undefined,
      idNumber: undefined,
      companyName: data.companyName,
      bankName: data.bankName,
      bankAddress: data.bankAddress,
      bankProvince: data.bankProvince,
      bankCity: data.bankCity,
      branchCode: data.branchCode,
      bankCountryCode: bankCountry,
      email: data.email,
      address: data.address,
      postCode: data.postCode,
      city: data.city,
      province: data.province,
    });

    return response;
  };

  const countryOptions = useMemo(() => {
    return countryStore.data.map((x) => {
      return { label: x.name, value: x.twoLetterCode };
    });
  }, [countryStore]);

  const onChangeForm = async () => {
    formControl.handleSubmit(onSubmit)();
  };

  useEffect(() => {
    formControl.setValue('province', undefined);
  }, [companyCountry, formControl.setValue]);
  useEffect(() => {
    formControl.setValue('bankProvince', undefined);
  }, [bankCountry, formControl.setValue]);

  const BankCountryName = countryOptions.find((option) => option.value === bankCountry);
  const ResidenceCountryName = countryOptions.find((option) => option.value === companyCountry);

  useEffect(() => {
    if (BankCountryName && ResidenceCountryName && currency) {
      $recipientMandatoryFieldsStore.events.get({
        ResidenceCountryCode: ResidenceCountryName?.value,
        BankCountryCode: BankCountryName?.value,
        Currency: currency,
        Type: TransferRecipientTypeEnum.Business,
      });
    }
  }, [BankCountryName, ResidenceCountryName, currency]);

  return (
    <div className='flex flex-col w-full'>
      <form onSubmit={formControl.handleSubmit(onSubmit)}>
        <div className='dark:bg-soft-black bg-white'>
          <AccountFormBlock
            formControl={formControl as UseFormReturn<AccountFormDataType, undefined>}
            countryOptions={countryOptions}
            countryStore={countryStore.data}
          />
          <CompanyFormBlock formControl={formControl as unknown as UseFormReturn<CompanyFormDataType, undefined>} />
          <AddressFormBlock formControl={formControl as UseFormReturn<AddressFormDataType, undefined>} countryOptions={countryOptions} />
        </div>
      </form>
      <Button type='submit' color='primary' className='px-28 mt-5 max-w-72' onClick={onChangeForm}>
        Add new recipient
      </Button>
      {recipientStore.data && <ConfirmModal icon={<ConfirmIcon />} title='A new recipient was added' />}
    </div>
  );
};
